import { Link, useParams } from "react-router-dom";
import { approvalBySlug, approvalByKey, whenRequired, APPROVALS, PHASES, PROJECT_TYPE_LABELS, APPROVAL_OVERRIDES, STATES_SEO, truncate } from "../../seo/explore-data";
import { PageSeo, Crumbs, CtaBlock, Disclaimer, Faq, SectionH, KvTable } from "./ExploreBits";
import NotFound from "../NotFound";

export default function ApprovalDetail() {
  const { slug } = useParams();
  const a = approvalBySlug(slug);
  if (!a) return <NotFound />;

  const ov = APPROVAL_OVERRIDES[a.key] || {};
  const triggers = whenRequired(a.key);
  const deps = (a.dependencies || []).map((k) => approvalByKey(k)).filter(Boolean);
  const phase = PHASES.find((p) => p.key === a.phase);
  const related = APPROVALS.filter((x) => x.phase === a.phase && x.key !== a.key).slice(0, 4);
  const stateVariants = (a.variants || []).filter((v) => v.states && v.states.length);
  const ptypes = (a.project_types || []).map((t) => PROJECT_TYPE_LABELS[t] || t);

  const faqs = [
    { q: `What is ${a.name}?`, a: a.plain_summary || a.description || "" },
    { q: `How long does ${a.name} take?`, a: `Around ${a.timeline_days || "—"} days as an indicative planning figure. Actual timelines vary by authority workload and application quality — verify with the concerned authority.` },
    { q: `Which authority grants ${a.name}?`, a: `${a.authority || a.department || "See statutory basis"}.${a.department ? ` Department: ${a.department}.` : ""}` },
    a.documents?.length ? { q: `What documents are required for ${a.name}?`, a: a.documents.join("; ") + "." } : null,
    a.always_conditional || a.conditional_reason
      ? { q: `Is ${a.name} always required?`, a: `${a.conditional_reason || "It applies conditionally depending on project specifics."} Verify applicability with the concerned authority.` }
      : { q: `When is ${a.name} required?`, a: triggers.length ? triggers.map((t) => t.text).join(". ") + "." : "Applicability depends on your project inputs — run the RegNav wizard for a project-specific answer." },
  ].filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="approval-detail-page">
      <PageSeo title={`${a.name} - Documents, Timeline & Process | RegNav`}
        description={truncate(a.plain_summary || a.description || `${a.name} for Indian power projects.`, 158)}
        path={`/approvals/${a.slug}/`} />
      <Crumbs items={[{ name: "Home", to: "/" }, { name: "Approvals", to: "/approvals" }, { name: a.name }]} />
      <h1 className="font-serif text-3xl sm:text-4xl text-[#0B1F3A]">{a.name} — documents, timeline &amp; process</h1>
      <p className="text-sm text-[#475569] mt-2">{phase ? `${phase.code} · ${phase.label}` : ""}{a.timeline_days ? ` · ~${a.timeline_days} days (indicative)` : ""}</p>
      {ov.notice && (
        <div className="bg-amber-50 border border-amber-300 border-l-4 border-l-amber-600 rounded-lg px-4 py-3 mt-5 text-sm text-amber-900" data-testid="approval-notice">
          <strong>Important:</strong> {ov.notice}
        </div>
      )}
      <div className="bg-white border border-[#0B1F3A]/10 rounded-xl px-5 py-4 mt-5">
        <p className="text-base text-[#0B1F3A]">{a.plain_summary}</p>
        {a.description && <p className="text-sm text-[#475569] mt-2">{a.description}</p>}
      </div>
      {ptypes.length > 0 && (
        <p className="mt-4">
          {ptypes.map((t) => <span key={t} className="inline-block bg-[#1D4ED8]/8 text-[#1D4ED8] rounded-full px-3 py-0.5 text-xs font-semibold mr-1.5 mb-1.5">{t}</span>)}
        </p>
      )}

      <SectionH>Statutory basis &amp; authority</SectionH>
      <KvTable rows={[
        ["Statutory basis", a.statutory_basis || "—"],
        ["Authority", a.authority || "—"],
        ["Department", a.department || "—"],
        ["Indicative timeline", `~${a.timeline_days || "—"} days (verify with the authority)`],
        a.fees_note ? ["Fees", a.fees_note] : null,
      ]} />

      {a.documents?.length > 0 && (
        <>
          <SectionH>Documents required</SectionH>
          <ul className="list-disc pl-6 text-sm text-[#0B1F3A] space-y-1.5">{a.documents.map((d) => <li key={d}>{d}</li>)}</ul>
        </>
      )}
      {a.sub_steps?.length > 0 && (
        <>
          <SectionH>Process steps</SectionH>
          <ol className="list-decimal pl-6 text-sm text-[#0B1F3A] space-y-1.5">{a.sub_steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </>
      )}

      {triggers.length > 0 && (
        <>
          <SectionH>When is this required?</SectionH>
          <ul className="list-disc pl-6 text-sm text-[#0B1F3A] space-y-1.5">
            {triggers.map((t) => <li key={t.name}><strong>{t.name}:</strong> {t.text}{t.logic === "any" ? " (any condition suffices)" : ""}</li>)}
          </ul>
        </>
      )}
      {a.conditional_reason && (
        <div className="bg-amber-50 border border-amber-300 border-l-4 border-l-amber-600 rounded-lg px-4 py-3 mt-5 text-sm text-amber-900">
          <strong>Conditional:</strong> {a.conditional_reason} — verify with the concerned authority.
        </div>
      )}

      {stateVariants.length > 0 && (
        <>
          <SectionH>State-specific notes</SectionH>
          {stateVariants.map((v) => (
            <div key={v.name || v.states.join(",")} className="bg-white border border-[#0B1F3A]/10 rounded-xl px-5 py-4 mb-3">
              <h3 className="font-serif font-semibold text-[#0B1F3A] text-sm">{v.name || v.states.join(", ")}</h3>
              <p className="text-xs text-[#475569] mt-1">States: {v.states.map((st, i) => {
                const sd = STATES_SEO.find((x) => x.name === st);
                return <span key={st}>{i > 0 && ", "}{sd ? <Link to={`/states/${sd.slug}`} className="text-[#1D4ED8] hover:underline">{st}</Link> : st}</span>;
              })}</p>
              {v.authority && <p className="text-xs text-[#475569] mt-1">Authority: {v.authority}</p>}
              {v.statutory_basis && <p className="text-xs text-[#475569] mt-1">Basis: {v.statutory_basis}</p>}
              {v.timeline_days && <p className="text-xs text-[#475569] mt-1">Indicative timeline: ~{v.timeline_days} days</p>}
            </div>
          ))}
        </>
      )}

      {deps.length > 0 && (
        <>
          <SectionH>Complete these first</SectionH>
          <ul className="list-disc pl-6 text-sm space-y-1.5">
            {deps.map((d) => <li key={d.key}><Link to={`/approvals/${d.slug}`} className="text-[#1D4ED8] hover:underline">{d.name}</Link> — {truncate(d.plain_summary, 120)}</li>)}
          </ul>
        </>
      )}
      {a.expert_note && (
        <>
          <SectionH>Solarian expert note</SectionH>
          <div className="bg-white border-l-4 border-[#059669] border border-[#0B1F3A]/10 rounded-xl px-5 py-4 text-sm text-[#475569] italic">{a.expert_note}</div>
        </>
      )}

      <Faq faqs={faqs} />

      {related.length > 0 && (
        <>
          <SectionH>Related approvals in this phase</SectionH>
          <ul className="list-disc pl-6 text-sm space-y-1">
            {related.map((r) => <li key={r.key}><Link to={`/approvals/${r.slug}`} className="text-[#1D4ED8] hover:underline">{r.name}</Link></li>)}
          </ul>
        </>
      )}
      <p className="text-sm text-[#475569] mt-6">
        See also: <Link to="/approvals" className="text-[#1D4ED8] hover:underline">all approvals</Link> · <Link to="/glossary" className="text-[#1D4ED8] hover:underline">glossary of terms</Link> · <Link to="/states" className="text-[#1D4ED8] hover:underline">state roadmaps</Link>
      </p>
      <CtaBlock />
      <Disclaimer />
    </div>
  );
}
